<script lang="ts" setup>
import { type SshKey } from '@/types'
import { STATUS_CODES } from '@/constants'
import { ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import BaseDeleteModal from '@/components/base/BaseDeleteModal.vue'

const props = defineProps<{
  show: boolean
  sshKey: SshKey
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'deleted', key: SshKey): void
}>()

const loading = ref(false)

const handleClose = () => {
  emits('close')
}

const handleDelete = async () => {
  loading.value = true

  try {
    const res = await useAxios<null, null>(
      'DELETE',
      `/account/ssh-keys/${props.sshKey.id}`,
      true
    )

    if (res && (res.status === STATUS_CODES.NO_CONTENT || res.status === STATUS_CODES.OK)) {
      emits('deleted', props.sshKey)
      handleClose()
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'SshKeyDeleteModal' })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <BaseDeleteModal
    :show="show"
    :title="$t('dashboard.account.sshKeys.delete.title')"
    :subtitle="sshKey.name"
    :confirm-text="$t('dashboard.account.sshKeys.delete.message')"
    :warning-text="$t('dashboard.account.sshKeys.delete.warning')"
    :loading="loading"
    :delete-button-text="$t('dashboard.account.sshKeys.delete.confirm')"
    @delete="handleDelete"
    @close="handleClose"
  >
    <template #preview>
      <div class="preview-row">
        <span class="preview-label">{{ $t('dashboard.account.sshKeys.columns.name') }}</span>
        <code>{{ sshKey.name }}</code>
      </div>
      <div class="preview-row">
        <span class="preview-label">{{ $t('dashboard.account.sshKeys.columns.fingerprint') }}</span>
        <code>{{ sshKey.fingerprint }}</code>
      </div>
    </template>
  </BaseDeleteModal>
</template>
